import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Page } from '@core/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';

/**
 * ### **TermsPrivacyPolicyComponent**
 *
 * **Selector:** ``<app-terms-privacy-policy>``
 *
 * Un componente es un bloque, que contiene un template, contiene estilos y contiene la lógica, en este caso es el
 * componente encargado de renderizar los terminos y condiciones o las politicas de privacidad.
 */
@Component({
  selector: 'app-terms-privacy-policy',
  imports: [TranslateModule],
  templateUrl: "./terms-privacy-policy.component.html",
  styleUrl: "./terms-privacy-policy.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermsPrivacyPolicy implements OnInit {
  private readonly pageService = inject(Page);
  private readonly modalService = inject(NgbModal);
  private readonly sanitizer = inject(DomSanitizer);

  /** Variable que guardara los terminos y condiciones*/
  protected readonly termsCondition = signal<any | null>(null);
  /** variable que guardara las politicas de privacidad*/
  protected readonly privacyPolicy = signal<any | null>(null);
  /** variable que guardara el titulo*/
  protected readonly title = signal<string>('');

  /**
   * Variable que guardara un estado que le permitirá asber si muestra o no los terminos y condicione,
   * en este caso el valor llega desde un componente padre.
   */
  readonly terms = signal<boolean>(false);

  /**
   * Elemento que refrencia al modal llamado content
   */
  private readonly modalContent = viewChild('content');

  /**
   * Función nativa que inicializa variables y funciones que se requieran para dar inicio a la carga del componente.
   * @param pageService  Parametro que contiene las funcionalidades de PageService
   * @param modalService Parametro que contiene las funcionalidades de NgbModal
   * @param sanitizer Parametro que contiene las funcionalidades de DomSanitizer
   */
  constructor() {
    this.getPrivacyPolicy();
    this.getTermsCondition();
  }

  /**
   * Función nativa de angular que se activa una vez se empieza a cargar el componenten, en este caso
   * pregunta por la variable terms y dependiendo su estado define un titulo.
   */
  ngOnInit() {
    this.title.set(this.terms() ? 'terms_condition' : 'privacy_policy');
  }

  /**
   *  Funcion encargada de abrir el modal y mostrar su respectivo contenido.
   *  El titulo de este se asigna dependiendo del valor que tenga terms.
   * @param terms parametro de tipo booleano que definira el titulo del modal.
   */
  open(terms: boolean): void {
    this.terms.set(terms);
    /** Se asigna el titulo en caso de ser true sera 'terms_condition' */
    this.title.set(terms ? 'terms_condition' : 'privacy_policy');
    this.modalService.open(this.modalContent, { size: 'lg' });
  }

  /**
   * Función encargada de obtener las politias de privacidad, para esto hace uso del servicio PageService,
   * una vez es capturado el siguiente paso es limpiar o vacunar la respuesta par que sea inyectada en el codigo fuente
   * esto con el objetivo de evitar inyecciones maliciosas de código.
   */
  private async getPrivacyPolicy(): Promise<void> {
    try {
      const privacy = await lastValueFrom(this.pageService.privacyPolicy());
      const privacyPolicy = this.sanitizer.bypassSecurityTrustHtml(privacy.language.content);
      this.privacyPolicy.set(privacyPolicy);
    } catch (error) {
      console.error('Error fetching privacy policy:', error);
    }
  }

  /**
   * Función encargada de obtener los terminos y condiciones, para esto hace uso del servicio PageService,
   * una vez es capturado el siguiente paso es limpiar o vacunar la respuesta par que sea inyectada en el codigo fuente
   * esto con el objetivo de evitar inyecciones maliciosas de código.
   */
  private async getTermsCondition(): Promise<void> {
    try {
      const terms = await lastValueFrom(this.pageService.termsCondition());
      const termsCondition = this.sanitizer.bypassSecurityTrustHtml(terms.language.content);
      this.termsCondition.set(termsCondition);
    } catch (error) {
      console.error('Error fetching terms and conditions:', error);
    }
  }
}
